import React, { useEffect, useState } from 'react';
import { css } from 'aphrodite';
import { useLocation } from 'react-router-dom';
import { Col, Row } from 'antd';
import dayjs from 'dayjs';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';

import TextBase from '../../common/text';
import { Colors } from '../../themes/Colors';
import ButtonGradient from '../../common/button-gradient';
import {
  MESSAGE_TYPE,
  messageManager,
  translate
} from '../../helper';
import { SVGImageComponentReact } from '../../themes/Images';
import { ACHBankAccountModal } from '../../common/ach/ACHBankAccountModal';
import { useGetPaymentMethods } from '../../hooks/usePayment';
import { usePaymentPolling } from '../../hooks/usePaymentPolling';
import {
  useCreateVendorSubscription,
  useGetTrialSubscriptionSettings
} from '../../hooks/useSubscriptions';
import { ConfirmModal } from '../../common/confirm-modal';

import { styles } from './styles';
import { AddPaymentMethodButton } from '../../common/new-card-button';
import { AddPaymentMethodModal } from '../../common/payments/AddPaymentMethodModal';
import AllPaymentMethods from '../../common/payments';
import CustomSpinner from '../../common/CustomSpinner';
import { resolveCssVarValue } from '../../utils/CommonFunction';
import PageTitleHeader from '../../common/page-title-header';

dayjs.extend(LocalizedFormat);

export const PaymentBase = ({ 
  title='cards',
  goBack, 
  goToEditCard,
  goAddCard
}) => {
  const { state } = useLocation();

  const { paymentMethods, isLoading, refetchPaymentMethods } = useGetPaymentMethods();
  const { createVendorSubscription, isCreatingSubscription } = useCreateVendorSubscription(
    onSuccess,
    onError
  );
  const { trialSubscriptionSettings } = useGetTrialSubscriptionSettings();

  const [selected, setSelected] = useState();
  const [showAchModal, setShowAchModal] = useState(false);
  const [showPaymentError, setShowPaymentError] = useState({ isVisible: false, message: '' });
  const [waitingForDefault, setWaitingForDefault] = useState(false);
  const [waitingForBankId, setWaitingForBankId] = useState(null);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);

  usePaymentPolling({ refetch: refetchPaymentMethods, enabled: waitingForDefault });
  const trialObj = trialSubscriptionSettings?.find(
    (item) => item?.subscription_price === state?.id
  );

  useEffect(() => {
    if (!waitingForBankId) return;
    if (waitingForDefault) {
      
    const found = (paymentMethods || []).find(
          pm => pm.is_default && (pm.id === waitingForBankId || pm.fingerprint === waitingForBankId || pm.last4 === waitingForBankId)
    );
      if (found) {
        setWaitingForDefault(false);
        setWaitingForBankId(null);
      }
    }
  }, [paymentMethods, waitingForDefault, waitingForBankId]);

  const closeError = () => {
    setShowPaymentError(false);
  };

  const handlePay = () => {
    createVendorSubscription({
      payment_method: selected?.djstripe_id,
      price: state?.id,
      is_trial: trialObj?.is_trial_active || false
    });
  };

  function onSuccess(data) {
    messageManager({
      type: MESSAGE_TYPE.success,
      messageShow: 'Successfully subscribed'
    });
    goBack();
  }

  function onError(error) {
    setShowPaymentError({ isVisible: true, message: error?.error });
  }

  return (
    <div className={css(styles.container)}>
      <Row justify="space-between">
        <Col>
          <Row>
            <div
              style={{ marginRight: "calc(var(--sp-lg)*1.5566037)" }}
              className="flex-row-center-center cursor-pointer"
              onClick={goBack}>
              <SVGImageComponentReact.IconArrowBack style={{width:"var(--fs-lg)", height:"var(--fs-lg)", color: '#1A5A78'}} />
            </div>
            <PageTitleHeader
              title={translate(title)}
              showBar={false}
              titleStyle={{ fontSize: 'calc(var(--fs-xs)*2.26415)', fontWeight: 600 }}
            />
          </Row>
        </Col>
      </Row>

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          width: '100%'
        }}>
        <div
          style={{
            width: 'calc(var(--fs-hero)*11.79245)',
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            backgroundColor: 'white',
            padding: 'calc(var(--sp-lg)*1.5094339)'
          }}>
          {trialObj?.is_trial_active && ((paymentMethods || []).filter(pm => (pm?.type || '').toLowerCase() === 'us_bank_account').length === 0) ? (
            <div
              style={{
                display: 'flex',
                marginBottom: 'var(--sp-lg)',
                justifyContent: 'center',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
              <TextBase
                style={{ opacity: 1 }}
                fontWeight={600}
                size="calc(var(--fs-xl)*1.132075)"
                color={Colors.black}>
                Get a Free trial
              </TextBase>
              <TextBase
                style={{ opacity: 1, textAlign: 'center' }}
                fontWeight={400}
                size="calc(var(--fs-md2)*1)"
                color={Colors.black}>
                Link bank account to get free subscription until{'\n '}
                {dayjs()
                  .add(trialObj?.trial_period_days || 0, 'day')
                  .format('MMM D, YYYY')}
              </TextBase>
            </div>
          ) : null}

          {isLoading ? (
              <CustomSpinner fullPage />
          ) : (
            <>
            <AddPaymentMethodButton onClick={() => setShowAddPaymentModal(true)} />

            {(isLoading || waitingForDefault) ? (
              <div style={{ height: 'calc(var(--fs-hero)*1.886)', width:'100%', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <CustomSpinner size={resolveCssVarValue('--fs-xl')*1.4150943} />
              </div>
            ) : (
                <AllPaymentMethods textAlign="center" onEditCard={goToEditCard} />
            )}
              
            </>
          )}
          {paymentMethods?.length && state?.price ? (
            <>
              {trialObj?.is_trial_active ? (
                <TextBase
                  style={{ opacity: 1, textAlign: 'center' }}
                  fontWeight={400}
                  size={'var(--fs-md2)'}
                  color={Colors.black}>
                  You won't be charged ${state?.price} until{'\n '}
                  {dayjs()
                    .add(trialObj?.trial_period_days || 0, 'day')
                    .format('MMM D, YYYY')}
                </TextBase>
              ) : null}
              <ButtonGradient
                onClick={handlePay}
                disabled={waitingForDefault || !(paymentMethods || []).some(pm => (pm?.type || '').toLowerCase()==='us_bank_account' && pm.is_default)}
                textStyle={{ fontWeight: 700, fontSize: 'var(--fs-md2)' }}
                title={trialObj?.is_trial_active ? 'Start your trial' : `PAY $${state?.price}`}
                style={{ marginTop: 'calc(var(--sp-lg)*1.69811)', width: '100%' }}
                loading={isCreatingSubscription}
              />
            </>
          ) : null}
        </div>
      </div>

      <ConfirmModal
        visible={showPaymentError.isVisible}
        status="danger"
        okBtnText="PAYMENTERROR"
        cancelBtnText="CANCEL"
        width="calc(var(--fs-hero)*9.4339622)"
        showButtons={false}
        handleCancel={closeError}>
        <TextBase
          style={{
            color: Colors.red,
            textAlign: 'center',
            marginBottom: 'calc(var(--sp-md2)*1.41509)'
          }}
          fontWeight={700}
          size={'calc(var(--fs-xl)*1.132075)'}
          color={Colors.titlePage}>
          Payment Failed
        </TextBase>
        <TextBase
          style={{
            color: Colors.black,
            textAlign: 'center',
            marginBottom: 'calc(var(--sp-md2)*1.41509)'
          }}
          fontWeight={400}
          size={'var(--fs-md2)'}
          color={Colors.titlePage}>
          {showPaymentError.message || 'we couldnt process the payment'}
        </TextBase>
        <ButtonGradient
          onClick={closeError}
          textStyle={{ fontWeight: 700, fontSize: 'var(--fs-md2)' }}
          title={`TRY AGAIN`}
          style={{ marginTop: 'calc(var(--sp-lg)*1.69811)', width: '100%' }}
          loading={isCreatingSubscription}
        />
      </ConfirmModal>
      <AddPaymentMethodModal
        open={showAddPaymentModal}
        onClose={() => setShowAddPaymentModal(false)}
        onAddBank={() => {
          setShowAddPaymentModal(false);
          setShowAchModal(true);
        }}
        onAddCard={() => {
          setShowAddPaymentModal(false);
          goAddCard();
        }}
      />
      
      <ACHBankAccountModal
        open={showAchModal}
        onClose={() => { 
          setShowAchModal(false); 
        }}
        onLinked={bank => { 
          setShowAchModal(false); 
          setWaitingForDefault(true);
          setWaitingForBankId(bank?.id || bank?.fingerprint || bank?.last4);
        }}
      />
    </div>
  );
}

export default PaymentBase;
